import { Injectable, inject } from '@angular/core';
import {
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { TenantConfigService } from './tenant-config.service';

/**
 * Prefijos de URL considerados "backend" para este interceptor.
 *
 * Se derivan automáticamente de todas las propiedades `api*` definidas en
 * `environment.ts`, lo que cubre tanto las URLs absolutas (`http://192.168...`)
 * como las relativas del dev server (`/local-proxy/...`).
 */
const BACKEND_URL_PREFIXES: string[] = Object.entries(environment)
    .filter(
        ([key, value]) =>
            key.startsWith('api') && typeof value === 'string' && value.length > 0
    )
    .map(([, value]) => value as string);

/**
 * Interceptor HTTP que agrega de forma transparente:
 *  - Headers de contexto: X-Tenant-Id, X-Application-Id
 *  - Query params de defaults (country, currency, channel, etc.)
 *
 * Solo aplica a requests dirigidos al backend del producto (derivados de
 * `environment.api*`). NO toca `/assets`, CDNs (Google Maps, Lottie, etc.)
 * ni cualquier otra URL fuera de esos prefijos.
 *
 * Ojo: este interceptor NO debe usarse para reglas de negocio sensibles.
 * Para autorización real, validar en backend.
 *
 * Para parámetros por operación puntual (p.ej. 'orders.search'),
 * usar un helper explícito en el service correspondiente y NO inferirlo
 * desde la URL acá.
 */
@Injectable()
export class TenantApiInterceptor implements HttpInterceptor {
    private readonly tenantConfig = inject(TenantConfigService);

    intercept(
        req: HttpRequest<unknown>,
        next: HttpHandler
    ): Observable<HttpEvent<unknown>> {
        // Si la config todavía no se cargó (no debería pasar gracias al
        // APP_INITIALIZER), pasamos el request tal cual.
        if (!this.tenantConfig.config()) {
            return next.handle(req);
        }

        // No aplicar a requests que no sean del backend (assets, CDNs, terceros).
        if (!this.isBackendRequest(req)) {
            return next.handle(req);
        }

        const defaults = this.tenantConfig.getApiDefaults();

        let clonedRequest = req.clone({});

        for (const [key, value] of Object.entries(defaults)) {
            // No pisar params que ya vienen seteados por el caller.
            if (!clonedRequest.params.has(key)) {
                clonedRequest = clonedRequest.clone({
                    params: clonedRequest.params.set(key, String(value))
                });
            }
        }

        return next.handle(clonedRequest);
    }

    private isBackendRequest(req: HttpRequest<unknown>): boolean {
        return BACKEND_URL_PREFIXES.some((prefix) => req.url.startsWith(prefix));
    }
}
